"use client";

import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PageFuturesLPSchema } from "@/sanity/pages/PageFuturesLPTemplate";
import Image from "@/helpers/Image";
import NextImage from "next/image";
import dots from "@/public/ecommerce/dots.png";

function XIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="shrink-0"
    >
      <path
        d="M1.5 1.5L10.5 10.5M10.5 1.5L1.5 10.5"
        stroke="#840C0C"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="14"
      height="11"
      viewBox="0 0 14 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="shrink-0"
    >
      <path
        d="M1.5 5.5L5.5 9.5L12.5 1.5"
        stroke="#12F0B4"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function ComparisonSection({
  data,
}: {
  data: PageFuturesLPSchema;
}) {
  if (
    !data.comparisonTitle &&
    !data.comparisonLeftLabel &&
    !data.comparisonRightLabel
  )
    return null;

  return (
    <section className="bg-[#f7f7f7] py-[10rem] lg:py-[20rem]">
      <div className="mx-auto w-[min(170rem,100%)] px-[4rem] flex flex-col items-center gap-[6rem]">
        {/* Header */}
        <div className="relative flex flex-col items-center gap-[1.25rem] text-center max-w-[102.75rem]">
          {data.comparisonTitle && (
            <div
              className="richtext richtext-center relative z-[1] flex flex-col gap-0 prose-h1:text-[5rem] lg:prose-h1:text-[7rem] prose-h1:tracking-[-0.35rem] prose-h1:leading-[1.15] prose-h2:text-[5rem] lg:prose-h2:text-[7rem] prose-h2:tracking-[-0.21rem] prose-h2:leading-none prose-h2:mb-[2rem]  [&_br]:hidden lg:[&_br]:block"
              style={{ textWrapStyle: "pretty" }}
            >
              <PortableText
                value={data.comparisonTitle}
                components={richTextComponent}
              />
            </div>
          )}

          {data.comparisonBody && (
            <p className="mb-0 font-geist font-normal text-[2rem] leading-[1.5] text-black/70 text-center max-w-[73.25rem]">
              {data.comparisonBody}
            </p>
          )}
        </div>

        {/* Cards */}
        <div className="flex flex-col lg:flex-row gap-[2.5rem] w-full max-w-[115rem]">
          {/* Left card */}
          <div className="flex-1 bg-white border border-black/10 rounded-[0.25rem] p-[4rem] flex flex-col gap-[3.25rem]">
            {data.comparisonLeftLabel && (
              <span className="font-geist font-semibold text-[2.5rem] leading-[1.33] text-black">
                {data.comparisonLeftLabel}
              </span>
            )}
            <ul className="flex flex-col gap-[2.25rem]">
              {data.comparisonLeftItems?.map((item, i) => (
                <li
                  key={item._key ?? i}
                  className="flex items-center gap-[1.5rem]"
                >
                  <XIcon />
                  <span className="font-geist font-medium text-[2.25rem] leading-normal text-black/[0.62]">
                    {item.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right card */}
          <div
            className="flex-1 rounded-[0.25rem] p-[4rem] flex flex-col gap-[3.75rem] overflow-hidden relative"
            style={{ background: "#0d2922" }}
          >
            <NextImage
              src={dots}
              alt=""
              fill
              className="object-cover opacity-70 mix-blend-luminosity"
            />
            {/* Logo or label */}
            {data.comparisonRightLogo ? (
              <Image
                src={data.comparisonRightLogo}
                alt={data.comparisonRightLabel ?? ""}
                className="relative z-10 !h-[2.9rem] !w-auto self-start"
              />
            ) : (
              data.comparisonRightLabel && (
                <span className="relative z-10 self-start font-geist font-semibold text-[2.5rem] leading-[1.33] text-[#12F0B4]">
                  {data.comparisonRightLabel}
                </span>
              )
            )}
            <ul className="relative z-10 flex flex-col gap-[2.25rem]">
              {data.comparisonRightItems?.map((item, i) => (
                <li
                  key={item._key ?? i}
                  className="flex items-center gap-[1.5rem]"
                >
                  <CheckIcon />
                  <span className="font-geist font-medium text-[2.25rem] leading-normal text-white/[0.62]">
                    {item.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom text */}
        {data.comparisonBottomText && (
          <div className="richtext richtext-center prose-p:text-[2rem] prose-p:leading-[1.5] prose-p:text-black/70 max-w-[64.5rem] [&_br]:hidden lg:[&_br]:block">
            <PortableText
              value={data.comparisonBottomText}
              components={richTextComponent}
            />
          </div>
        )}
      </div>
    </section>
  );
}
